import { z } from "zod";
import { idSchema, instantSchema, reasonSchema } from "@amber/contracts";
import { cadenceTierSchema, languageCodeSchema, planRevisionStateSchema } from "./records.js";

export const PROFILES_ACTIONS = {
  editPlan: "profiles.edit_plan",
  pausePerson: "profiles.pause_person",
  resumePerson: "profiles.resume_person",
} as const;

export const EDITOR_PLAN_REASON = "Only Editors and above can change coverage plans";
export const EDITOR_PAUSE_PERSON_REASON = "Only Editors and above can pause or resume a person";

export const HISTORY_WINDOWS = ["2_weeks", "4_months", "1_year", "2_years", "3_years", "5_years"] as const;
export const historyWindowSchema = z.enum(HISTORY_WINDOWS);
export type HistoryWindow = z.infer<typeof historyWindowSchema>;
export const STARTING_HISTORY_WINDOW: HistoryWindow = "2_weeks";
export const HISTORY_WINDOW_LABELS: Record<HistoryWindow, string> = {
  "2_weeks": "2 weeks",
  "4_months": "4 months",
  "1_year": "1 year",
  "2_years": "2 years",
  "3_years": "3 years",
  "5_years": "5 years",
};

export function windowStart(window: HistoryWindow, monitoringStart: Date): Date {
  const start = new Date(monitoringStart);
  if (window === "2_weeks") start.setUTCDate(start.getUTCDate() - 14);
  else if (window === "4_months") start.setUTCMonth(start.getUTCMonth() - 4);
  else start.setUTCFullYear(start.getUTCFullYear() - Number(window.slice(0, 1)));
  return start;
}

export const HISTORY_OPTIONS = ["default", "2_weeks", "4_months", "1_year", "since"] as const;
export const historyOptionSchema = z.enum(HISTORY_OPTIONS);
export type HistoryOption = z.infer<typeof historyOptionSchema>;

export const HISTORY_OPTION_LABELS: Record<HistoryOption, string> = {
  default: "Default",
  "2_weeks": "2 weeks",
  "4_months": "4 months",
  "1_year": "1 year",
  since: "Since a date",
};

export const planHistorySchema = z
  .strictObject({ option: historyOptionSchema, since: z.iso.date().nullable() })
  .refine((h) => (h.option === "since") === (h.since !== null), {
    message: "Pick the date to collect from",
    path: ["since"],
  });
export type PlanHistory = z.infer<typeof planHistorySchema>;

export const planSourceEntrySchema = z.strictObject({
  source_id: idSchema("src"),
  cadence_tier: cadenceTierSchema,
  required: z.boolean(),
});
export type PlanSourceEntry = z.infer<typeof planSourceEntrySchema>;

export const planOwnerSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("person"), profile_id: idSchema("prf") }),
  z.strictObject({ kind: z.literal("shared_source"), source_id: idSchema("src") }),
]);
export type PlanOwner = z.infer<typeof planOwnerSchema>;

export const savePlanRequestSchema = z.strictObject({
  base_revision: z.number().int().positive().nullable(),
  sources: z
    .array(planSourceEntrySchema)
    .refine((s) => new Set(s.map((e) => e.source_id)).size === s.length, "A source is listed twice"),
  history: planHistorySchema,
  rationale: z.string().trim().min(1, "Say why these sources are monitored").max(2000),
  review_date: z.iso.date(),
  languages: z.array(languageCodeSchema).default([]),
  reason: reasonSchema,
});
export type SavePlanRequest = z.infer<typeof savePlanRequestSchema>;

export const PLAN_STATE_LABELS: Record<z.infer<typeof planRevisionStateSchema>, string> = {
  recorded: "Changes pending",
  blocked: "Changes pending · Blocked",
  applied: "Applied",
  superseded: "Superseded",
};

export const planRevisionViewSchema = z.strictObject({
  id: idSchema("plr"),
  revision: z.number().int().positive(),
  state: planRevisionStateSchema,
  state_label: z.string(),
  sources: z.array(planSourceEntrySchema),
  history: planHistorySchema,
  history_custom: z.boolean(),
  back_to: z.iso.date(),
  monitoring_start: instantSchema,
  rationale: z.string(),
  review_date: z.iso.date(),
  languages: z.array(languageCodeSchema),
  reason: z.string(),
  recorded_by: z.string(),
  recorded_at: instantSchema,
  applied_at: instantSchema.nullable(),
});
export type PlanRevisionView = z.infer<typeof planRevisionViewSchema>;

export const planViewSchema = z.strictObject({
  id: idSchema("pln"),
  owner: planOwnerSchema,
  latest: planRevisionViewSchema,
  applied: planRevisionViewSchema.nullable(),
  pending: z.boolean(),
  history: z.array(planRevisionViewSchema),
});
export type PlanView = z.infer<typeof planViewSchema>;

export interface AppliedPlanSource {
  readonly planId: string;
  readonly revisionId: string;
  readonly owner: PlanOwner;
  readonly ownerState: string | null;
  readonly sourceId: string;
  readonly cadenceTier: z.infer<typeof cadenceTierSchema>;
  readonly required: boolean;
  readonly backTo: string;
  readonly history: PlanHistory;
  readonly monitoringStart: Date;
}

export interface PlanMembership {
  readonly planId: string;
  readonly owner: PlanOwner;
  readonly ownerName: string | null;
  readonly cadenceTier: z.infer<typeof cadenceTierSchema>;
  readonly required: boolean;
  readonly applied: boolean;
  readonly pending: boolean;
  /** Whether the plan's latest revision, recorded or applied, includes the source. */
  readonly latest: boolean;
}

export function historyBackTo(
  history: PlanHistory,
  monitoringStart: Date,
  central: HistoryWindow = STARTING_HISTORY_WINDOW,
): string {
  if (history.option === "since") return history.since ?? new Date(monitoringStart).toISOString().slice(0, 10);
  return windowStart(history.option === "default" ? central : history.option, monitoringStart)
    .toISOString()
    .slice(0, 10);
}

export const LIFECYCLE_LABELS = {
  draft: "Draft",
  active: "Tracked",
  paused: "Paused",
  archived: "Retired",
  incidental: "Not tracked",
} as const;

export const VERIFICATION_STATES = ["current", "stale", "never"] as const;
export type VerificationState = (typeof VERIFICATION_STATES)[number];

export const profileListRowSchema = z.strictObject({
  id: idSchema("prf"),
  name: z.string(),
  lifecycle_state: z.enum(["draft", "active", "paused", "archived", "incidental"]),
  state_label: z.string(),
  plan: z
    .strictObject({
      pending: z.boolean(),
      applied: z.boolean(),
      sources: z.number().int().nonnegative(),
      review_date: z.iso.date(),
    })
    .nullable(),
  role: z.string().nullable(),
  languages: z.array(languageCodeSchema),
  aliases: z.array(z.string()),
  wikidata_id: z.string().nullable(),
  wikipedia: z.array(z.url()),
  accounts: z.array(z.string()),
  picture_url: z.string().nullable(),
  discovery: z.enum(["none", "queued", "finding", "ready", "failed"]),
  owner: z.strictObject({ id: idSchema("usr"), name: z.string() }).nullable(),
  verification: z.strictObject({
    state: z.enum(VERIFICATION_STATES),
    last_verified_at: instantSchema.nullable(),
  }),
});
export type ProfileListRow = z.infer<typeof profileListRowSchema>;

export const monitorSourceRequestSchema = z.strictObject({
  base_revision: z.number().int().positive().nullable(),
  cadence_tier: cadenceTierSchema,
  history: planHistorySchema,
  rationale: z.string().trim().min(1, "Say why this source is monitored").max(2000),
  review_date: z.iso.date(),
  reason: reasonSchema,
});
